import PropTypes from 'prop-types';
import React, { useEffect, useContext, useRef } from 'react';

import { ToastContainer } from 'react-toastify';
import { Badge, Card, Tabs } from '@opendate-app/setlist';

import AddAttendees from "./AddAttendees";
import ImportAttendees from "./ImportAttendees";
import TransferTickets from "./TransferTickets";
import Checkout from "./Checkout";
import TimeLimitReached from "./TimeLimitReached";
import EventDetailsApp from '../../EventDetails/startup/EventDetailsApp';

import { ActionCableContext } from '../startup/ConfirmAttendeesApp';
import { calendarClassificationBadge } from '../../../shared/calendarClassificationBadge';

import {
  ADD_ATTENDEES_MENU_ITEM,
  IMPORT_ATTENDEES_MENU_ITEM,
  TRANSFER_TICKETS_MENU_ITEM
} from '../constants/confirmAttendeesConstants';

const axios = require('axios').default;

const https = require('https');
const httpsAgent = new https.Agent({ keepAlive: true });

const MENU_ITEMS = [
  { value: ADD_ATTENDEES_MENU_ITEM, label: "Add Attendees" },
  { value: IMPORT_ATTENDEES_MENU_ITEM, label: "Import Attendees" },
  { value: TRANSFER_TICKETS_MENU_ITEM, label: "Transfer Tickets" },
];

const ConfirmAttendees = ({
  csrfToken,
  team,
  currentUser,
  confirm,
  confirmChanged,
  activeMenuItem,
  activeMenuItemChanged,
  ticketReservation,
  orderType,
  orderTypeChanged,
  ticketTypes,
  addOns,
  ticketTypesChanged,
  addOnsChanged,
  buildTicketReservation,
  createTicketReservation,
  updateTicketReservation,
  rootUrl,
  ticketReservationChanged,
  isPlacingOrder,
  isPlacingOrderChanged,
  checkoutTimePercentageRemaining,
  checkoutTimePercentageRemainingChanged,
  timeLimitReached,
  timeLimitReachedChanged,
  deleteTicketReservation,
  manualTypes,
  customFieldValues,
  customFieldValueChanged,
  promoters,
  buyers,
  seatsIOPublicKey,
  seatsIOChart,
  seatsIOChartChanged,
  seatsIOSelectedObjects,
  seatsIOSelectedObjectsChanged,
  seatingChartEvents,
  seatingEventHoldsByEventId,
  generalAdmissionHolds,
  selectedGeneralAdmissionHoldId,
  generalAdmissionHoldSelected,
  defaultCountry,
  giftCardsEnabled
}) => {
  const cable = useContext(ActionCableContext).cable;
  const latestTicketTypes = useRef(ticketTypes);
  const latestAddOns = useRef(addOns);
  latestTicketTypes.current = ticketTypes;
  latestAddOns.current = addOns;
  const [badgeColor, badgeLabel] = calendarClassificationBadge(confirm.calendar_classification);

  useEffect(() => {
    if (cable) {
      const subscription = cable.subscriptions.create(
        {
          channel: "InternalConfirmChannel",
          id: confirm.id
        },
        {
          received: (message) => {
            if (message.ticket_types && Array.isArray(message.ticket_types)) {
              const updatesMap = new Map(
                message.ticket_types.map(update => [update.id, update])
              );
              const merged = latestTicketTypes.current.map(tt => {
                const update = updatesMap.get(tt.id);
                return update ? { ...tt, ...update } : tt;
              });
              ticketTypesChanged(merged);
            }
            if (message.add_ons && Array.isArray(message.add_ons)) {
              const updatesMap = new Map(
                message.add_ons.map(update => [update.id, update])
              );
              const merged = latestAddOns.current.map(ao => {
                const update = updatesMap.get(ao.id);
                return update ? { ...ao, ...update } : ao;
              });
              addOnsChanged(merged);
            }
          }
        }
      );

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [cable, confirm.id]);

  const freeHoldTokens = () => {
    var orderItems = ticketTypes.filter((tt) =>
      tt.quantity && tt.quantity > 0
    );

    var addOnOrderItems = addOns.filter((ao) =>
      ao.quantity && ao.quantity > 0
    );

    var seatsioHoldTokens = [];

    if(orderItems.some((i) => i.seatsIOObjects && i.seatsIOObjects.length > 0)){
      seatsioHoldTokens = [...seatsioHoldTokens, seatsIOChart.holdToken];
    }

    var addOnHoldTokens = [
      ...new Set(
        addOnOrderItems
          .filter((item) => item.seatsIOObjects)
          .map((item) => item.seatsIOObjects)
          .flat()
          .map((object) => object.hold_token)
      )
    ];

    seatsioHoldTokens = [
      ...seatsioHoldTokens,
      ...addOnHoldTokens
    ];

    if (seatsioHoldTokens.length) {
      axios.get(`/confirms/${confirm.id}/ticket_reservations/expire_hold_tokens`, {
        params: {
          hold_tokens: seatsioHoldTokens
        },
        httpsAgent: httpsAgent
      })
      .catch(_error => {});
    }
  };

  useEffect(() => {
    window.onbeforeunload = function() {
      if(!isPlacingOrder) {
        freeHoldTokens();
        e.preventDefault();
        return "";
      }
    }
  }, [ticketTypes, addOns, isPlacingOrder]);

  return(<div>
    <ToastContainer icon={false} />
    <div className="row m-0">
      <div className="col-12">
        <div className="page-title d-flex align-items-center">
          { confirm.name }
          <span className="ml-3">
            <Badge size="sm" color={badgeColor}>{badgeLabel}</Badge>
          </span>
        </div>
        <EventDetailsApp
          csrfToken={csrfToken}
          team={team}
          currentUser={currentUser}
          confirm={{...confirm}}
          confirmSaved={(savedConfirm) => {
            confirmChanged(savedConfirm);
          }}
          buyers={[...buyers]}
          promoters={promoters} />
      </div>
    </div>
    <div className="row mb-4 mx-0">
      <div className="col-12">
        <Card padding="none" className="overflow-hidden">
          <div className="row mx-0">
            <div className="col-12 col-md-auto attendees-rail"
                 style={{
                   "padding": "22px 20px",
                   "minWidth": "230px",
                   "background": "var(--sl-bg-secondary)"
                 }}>
              <h3 style={{"fontSize": "24px", "marginBottom": "28px"}}>
                <strong>
                  Attendees
                </strong>
              </h3>
              <Tabs orientation="vertical"
                    variant="button-border"
                    track={false}
                    aria-label="Attendees"
                    items={MENU_ITEMS}
                    value={activeMenuItem || ADD_ATTENDEES_MENU_ITEM}
                    onChange={activeMenuItemChanged}
                    collapseBelow={0} />
            </div>
            <div className="col-12 col-md ticketing-content"
                 style={{"padding": "35px 40px"}}>
              {activeMenuItem === IMPORT_ATTENDEES_MENU_ITEM ? (
                <ImportAttendees team={team}
                                  csrfToken={csrfToken}
                                  confirm={confirm} />
              ) : activeMenuItem === TRANSFER_TICKETS_MENU_ITEM ? (
                <TransferTickets team={team}
                                  csrfToken={csrfToken}
                                  confirm={confirm} />
              ) : timeLimitReached ? (
                <TimeLimitReached team={team}
                                  csrfToken={csrfToken}
                                  confirm={confirm} />
              ) : !ticketReservation.id ? (
                <AddAttendees csrfToken={csrfToken}
                              currentUser={currentUser}
                              confirm={confirm}
                              orderType={orderType}
                              orderTypeChanged={orderTypeChanged}
                              ticketTypes={ticketTypes}
                              addOns={addOns}
                              ticketTypesChanged={ticketTypesChanged}
                              addOnsChanged={addOnsChanged}
                              buildTicketReservation={buildTicketReservation}
                              createTicketReservation={createTicketReservation}
                              ticketReservation={ticketReservation}
                              manualTypes={manualTypes}
                              seatsIOPublicKey={seatsIOPublicKey}
                              seatsIOChart={seatsIOChart}
                              seatsIOChartChanged={seatsIOChartChanged}
                              seatsIOSelectedObjects={seatsIOSelectedObjects}
                              seatsIOSelectedObjectsChanged={seatsIOSelectedObjectsChanged}
                              seatingChartEvents={seatingChartEvents}
                              seatingEventHoldsByEventId={seatingEventHoldsByEventId}
                              generalAdmissionHolds={generalAdmissionHolds}
                              selectedGeneralAdmissionHoldId={selectedGeneralAdmissionHoldId}
                              generalAdmissionHoldSelected={generalAdmissionHoldSelected}
                              team={team} />
              ) : (
                <Checkout team={team}
                          csrfToken={csrfToken}
                          confirm={confirm}
                          ticketTypes={ticketTypes}
                          ticketReservation={ticketReservation}
                          ticketReservationChanged={ticketReservationChanged}
                          updateTicketReservation={updateTicketReservation}
                          rootUrl={rootUrl}
                          checkoutTimePercentageRemaining={checkoutTimePercentageRemaining}
                          checkoutTimePercentageRemainingChanged={checkoutTimePercentageRemainingChanged}
                          timeLimitReachedChanged={timeLimitReachedChanged}
                          deleteTicketReservation={deleteTicketReservation}
                          isPlacingOrder={isPlacingOrder}
                          isPlacingOrderChanged={isPlacingOrderChanged}
                          customFieldValues={customFieldValues}
                          customFieldValueChanged={customFieldValueChanged}
                          addOns={addOns}
                          orderType={orderType}
                          giftCardsEnabled={giftCardsEnabled}
                          defaultCountry={defaultCountry} />
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  </div>);
};

ConfirmAttendees.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  confirmChanged: PropTypes.func.isRequired,
  activeMenuItem: PropTypes.string,
  activeMenuItemChanged: PropTypes.func.isRequired,
  ticketReservation: PropTypes.object,
  orderType: PropTypes.string,
  orderTypeChanged: PropTypes.func.isRequired,
  ticketTypes: PropTypes.array,
  addOns: PropTypes.array,
  ticketTypesChanged: PropTypes.func.isRequired,
  addOnsChanged: PropTypes.func.isRequired,
  buildTicketReservation: PropTypes.func.isRequired,
  createTicketReservation: PropTypes.func.isRequired,
  updateTicketReservation: PropTypes.func.isRequired,
  rootUrl: PropTypes.string.isRequired,
  ticketReservationChanged: PropTypes.func.isRequired,
  isPlacingOrder: PropTypes.bool,
  isPlacingOrderChanged: PropTypes.func.isRequired,
  checkoutTimePercentageRemaining: PropTypes.number,
  checkoutTimePercentageRemainingChanged: PropTypes.func.isRequired,
  timeLimitReached: PropTypes.bool,
  timeLimitReachedChanged: PropTypes.func.isRequired,
  deleteTicketReservation: PropTypes.func.isRequired,
  manualTypes: PropTypes.array,
  customFieldValues: PropTypes.array,
  customFieldValueChanged: PropTypes.func.isRequired,
  buyers: PropTypes.array,
  promoters: PropTypes.array,
  seatsIOPublicKey: PropTypes.string,
  seatsIOChart: PropTypes.object,
  seatsIOChartChanged: PropTypes.func.isRequired,
  seatsIOSelectedObjects: PropTypes.array,
  seatsIOSelectedObjectsChanged: PropTypes.func.isRequired,
  seatingChartEvents: PropTypes.array,
  seatingEventHoldsByEventId: PropTypes.object,
  generalAdmissionHolds: PropTypes.array,
  selectedGeneralAdmissionHoldId: PropTypes.number,
  generalAdmissionHoldSelected: PropTypes.func.isRequired,
  ticketTypesChanged: PropTypes.func.isRequired,
  addOnsChanged: PropTypes.func.isRequired,
  defaultCountry: PropTypes.string,
  giftCardsEnabled: PropTypes.bool
};

ConfirmAttendees.defaultProps = {
  selectedGeneralAdmissionHoldId: null,
};

export default ConfirmAttendees;
